import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  BellIcon,
  BookIcon,
  BookmarkIcon,
  ChevronIcon,
  GlobeIcon,
  HomeIcon,
  MoonIcon,
  PinIcon,
  ShieldIcon,
  UserIcon,
} from '../components/icons';
import { PickerModal } from '../components/profile/picker-modal';
import { useTLoose } from '../i18n/t-loose';
import { signOut, useAuth } from '../hooks/use-auth';
import { useProfileQuery, useUpdateProfileMutation } from '../hooks/use-profile-query';
import { useSubscribedChannelsQuery } from '../hooks/use-channels-query';
import { useSavedQuery } from '../hooks/use-feed-query';
import {
  sendPasswordResetEmail,
  useDeleteAccountMutation,
  useNotificationPreferencesQuery,
  useUpdatePreferencesMutation,
} from '../hooks/use-account-actions';
import { useTheme } from '../theme/theme-context';
import {
  COUNTRY_NAMES,
  getCountryName,
  getFlag,
} from '../config/reference-data';
import {
  findActiveLanguage,
  useActivePlatformLanguageOptions,
  useKnownLanguageOptions,
} from '../hooks/use-platform-languages-query';
import {
  AGE_RANGE_KEYS,
  INTEREST_LABEL_KEY_BY_SLUG,
  INTEREST_OPTIONS,
  LIFE_STAGE_KEYS,
  TIME_IN_COUNTRY_KEYS,
  VISA_TYPE_KEYS,
} from '../config/profile-options';
import type { UserProfile } from '../types';

type PickerKey =
  | 'primaryLanguage'
  | 'knownLanguages'
  | 'homeCountry'
  | 'currentCountry'
  | 'lifeStage'
  | 'visaType'
  | 'timeInCountry'
  | 'ageRange'
  | 'interests'
  | null;

export function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tLoose = useTLoose();
  const { user } = useAuth();
  const profile = useProfileQuery();
  const subscribed = useSubscribedChannelsQuery();
  const saved = useSavedQuery();
  const updateProfile = useUpdateProfileMutation();
  const notifPrefs = useNotificationPreferencesQuery();
  const updatePrefs = useUpdatePreferencesMutation();
  const deleteAccount = useDeleteAccountMutation();
  const { mode, setMode } = useTheme();

  const [picker, setPicker] = useState<PickerKey>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [passwordToast, setPasswordToast] = useState<string | null>(null);

  // Fetched from `/platform-languages/active` — same source of truth as
  // the mobile picker. Falls back to the bundled list while in flight.
  const activeLanguages = useActivePlatformLanguageOptions();
  // Broader curated list — only used by the knownLanguages picker so a
  // user can declare any of ~30 languages, not just those the platform
  // translates to. Falls back to activeLanguages while the query
  // hydrates.
  const knownPickerLanguages = useKnownLanguageOptions();

  const p: UserProfile | undefined = profile.data;
  const displayName = p?.displayName ?? (user?.user_metadata?.display_name as string | undefined) ?? user?.email?.split('@')[0] ?? 'You';
  const initial = displayName.charAt(0).toUpperCase();
  const primaryLang = findActiveLanguage(activeLanguages, p?.primaryLanguage);
  const channelCount = subscribed.data?.length ?? 0;
  const savedCount = saved.data?.length ?? 0;
  const memberSince = useMemo(() => {
    const iso = (p?.createdAt ?? user?.created_at) as string | undefined;
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }, [p?.createdAt, user?.created_at]);

  // 10-field completion calculation — mirrors mobile.
  const completion = useMemo(() => {
    if (!p) return { pct: 0, filled: 0, total: 10 };
    const fields = [
      !!p.displayName,
      !!p.primaryLanguage,
      p.knownLanguages && p.knownLanguages.length > 0,
      !!p.homeCountry,
      !!p.currentCountry,
      !!p.lifeStage,
      !!p.visaType,
      !!p.timeInCountry,
      !!p.ageRange,
      p.interests && p.interests.length > 0,
    ];
    const filled = fields.filter(Boolean).length;
    return { filled, total: fields.length, pct: Math.round((filled / fields.length) * 100) };
  }, [p]);

  // Country list — uses our COUNTRY_NAMES dictionary; sorted alphabetically.
  const countryOptions = useMemo(
    () =>
      Object.entries(COUNTRY_NAMES)
        .map(([code, label]) => ({
          key: code,
          label,
          hint: code,
          leading: <span style={{ fontSize: 18 }}>{getFlag(code)}</span>,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
  const languageOptions = useMemo(
    () =>
      activeLanguages.map((l) => ({
        key: l.code,
        label: l.name,
        hint: l.native,
        leading: <span style={{ fontSize: 18 }}>{l.flag}</span>,
      })),
    [activeLanguages],
  );
  // Known-languages picker uses the broader curated set when loaded;
  // falls back to active list during cache hydration to keep the UI
  // populated.
  const knownLanguageOptions = useMemo(
    () => {
      const source =
        knownPickerLanguages.length > 0 ? knownPickerLanguages : activeLanguages;
      return source.map((l) => ({
        key: l.code,
        label: l.name,
        hint: l.native,
        leading: <span style={{ fontSize: 18 }}>{l.flag}</span>,
      }));
    },
    [knownPickerLanguages, activeLanguages],
  );
  // Object.entries() strips the literal-string types from the *_KEYS
  // maps to plain `string`, which exceeds typed-options ParseKeys — use
  // the `tLoose` wrapper for these. Typo safety on the literal values
  // is enforced statically by `shared/i18n/scripts/lint-call-sites.ts`.
  const lifeStageOptions = useMemo(
    () => Object.entries(LIFE_STAGE_KEYS).map(([key, labelKey]) => ({ key, label: tLoose(labelKey) })),
    [tLoose],
  );
  const visaOptions = useMemo(
    () => Object.entries(VISA_TYPE_KEYS).map(([key, labelKey]) => ({ key, label: tLoose(labelKey) })),
    [tLoose],
  );
  const timeInCountryOptions = useMemo(
    () => Object.entries(TIME_IN_COUNTRY_KEYS).map(([key, labelKey]) => ({ key, label: tLoose(labelKey) })),
    [tLoose],
  );
  const ageRangeOptions = useMemo(
    () => Object.entries(AGE_RANGE_KEYS).map(([key, labelKey]) => ({ key, label: tLoose(labelKey) })),
    [tLoose],
  );
  const interestOptions = useMemo(
    () => INTEREST_OPTIONS.map((o) => ({ key: o.key, label: tLoose(o.labelKey) })),
    [tLoose],
  );

  const onLogout = async () => {
    await signOut();
    navigate('/');
  };

  const onChangePassword = async () => {
    if (!user?.email) return;
    try {
      await sendPasswordResetEmail(user.email);
      setPasswordToast(t('web.profile.passwordReset.success', { email: user.email }));
    } catch (err) {
      setPasswordToast(
        t('web.profile.passwordReset.error', {
          error: (err as Error).message ?? t('web.profile.passwordReset.errorFallback'),
        }),
      );
    }
    window.setTimeout(() => setPasswordToast(null), 6_000);
  };

  const onDeleteAccount = async () => {
    try {
      await deleteAccount.mutateAsync();
      navigate('/');
    } catch (err) {
      setPasswordToast(
        t('web.profile.passwordReset.error', {
          error: (err as Error).message ?? t('web.profile.passwordReset.errorFallback'),
        }),
      );
      setConfirmDelete(false);
    }
  };

  const closePicker = () => setPicker(null);

  return (
    <div className="nw-app nw-app--two">
      {/* Sidebar — anchor-style links scroll to each section. */}
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.profile.sidebar.label')}</div>
        <a className="nw-side__item is-active" href="#section-account">
          <UserIcon size={16} />
          <span className="nw-side__item-label">{t('web.profile.sidebar.account')}</span>
        </a>
        <a className="nw-side__item" href="#section-language">
          <GlobeIcon size={16} />
          <span className="nw-side__item-label">{t('web.profile.sidebar.languages')}</span>
        </a>
        <a className="nw-side__item" href="#section-library">
          <BookmarkIcon size={16} />
          <span className="nw-side__item-label">{t('web.profile.sidebar.library')}</span>
        </a>
        <a className="nw-side__item" href="#section-prefs">
          <BellIcon size={16} />
          <span className="nw-side__item-label">{t('web.profile.sidebar.preferences')}</span>
        </a>
        <a className="nw-side__item" href="#section-account-actions">
          <ShieldIcon size={16} />
          <span className="nw-side__item-label">{t('web.profile.sidebar.accountActions')}</span>
        </a>
        <div className="nw-side__divider" />
        <button
          type="button"
          className="nw-side__item"
          onClick={onLogout}
          style={{ color: 'var(--error)' }}
        >
          <span className="nw-side__item-label">{t('web.profile.sidebar.signOut')}</span>
        </button>
      </aside>

      <section className="nw-feed">
        <div className="nw-feed__head">
          <div>
            <h1 className="nw-feed__title">{t('web.profile.title')}</h1>
            <p className="nw-feed__sub">{t('web.profile.subtitle')}</p>
          </div>
        </div>

        {/* Profile hero with avatar + completion ring + stats */}
        <div className="nw-profile-hero">
          <div className="nw-profile-avatar">
            {p?.avatarUrl ? (
              <img
                src={p.avatarUrl}
                alt={displayName}
                referrerPolicy="no-referrer"
              />
            ) : (
              initial
            )}
          </div>
          <div className="nw-profile-hero__body">
            <h2 className="nw-profile-name">{displayName}</h2>
            <p className="nw-profile-meta">
              {user?.email ?? '—'} · {t('web.profile.hero.readingIn', { language: primaryLang.name })}
              {memberSince ? ` · ${t('web.profile.hero.memberSince', { date: memberSince })}` : ''}
            </p>
            <div className="nw-stat-row">
              <div>
                <div className="nw-stat__num">{channelCount}</div>
                <div className="nw-stat__label">{t('web.profile.hero.stats.channels')}</div>
              </div>
              <div>
                <div className="nw-stat__num">{p?.knownLanguages?.length ?? 0}</div>
                <div className="nw-stat__label">{t('web.profile.hero.stats.knownLangs')}</div>
              </div>
              <div>
                <div className="nw-stat__num">{savedCount}</div>
                <div className="nw-stat__label">{t('web.profile.hero.stats.saved')}</div>
              </div>
              <div>
                <div className="nw-stat__num">{completion.pct}%</div>
                <div className="nw-stat__label">{t('web.profile.hero.stats.profile')}</div>
              </div>
            </div>
          </div>
        </div>

        {passwordToast && (
          <div className="nw-toast" role="status">
            {passwordToast}
          </div>
        )}

        {/* YOUR LIBRARY */}
        <SectionHeader id="section-library" label={t('settings.sections.library')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsRow
            icon="bookmark"
            tone="green"
            label={t('settings.rows.savedArticles')}
            value={String(savedCount)}
            onClick={() => navigate('/saved')}
            chevron
          />
          <SettingsRow
            icon="globe"
            tone="blue"
            label={t('settings.rows.followedChannels')}
            value={String(channelCount)}
            onClick={() => navigate('/explore')}
            chevron
            last
          />
        </div>

        {/* YOUR WORLD — journey card */}
        <SectionHeader id="section-world" label={t('web.profile.journey.title')} />
        <div className="nw-journey-card">
          <FlagSquare code={p?.homeCountry} />
          <div className="nw-journey__body">
            <div className="nw-journey__title">
              <span>{getCountryName(p?.homeCountry) ?? t('settings.values.home')}</span>
              <ChevronIcon dir="right" size={14} stroke={2.4} />
              <span>{p?.city ?? getCountryName(p?.currentCountry) ?? t('settings.values.setYourCity')}</span>
            </div>
            <div className="nw-journey__sub">
              {[
                p?.region,
                getCountryName(p?.currentCountry),
                p?.timeInCountry
                  ? tLoose(TIME_IN_COUNTRY_KEYS[p.timeInCountry as keyof typeof TIME_IN_COUNTRY_KEYS] ?? '')
                  : t('settings.values.timeNotSet'),
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <FlagSquare code={p?.currentCountry} />
        </div>

        {/* LANGUAGE SETTINGS */}
        <SectionHeader id="section-language" label={t('settings.sections.language')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsRow
            icon="globe"
            tone="blue"
            label={t('settings.rows.primaryLanguage')}
            value={primaryLang.name}
            onClick={() => setPicker('primaryLanguage')}
            chevron
          />
          <SettingsRow
            icon="book"
            tone="green"
            label={t('settings.rows.knownLanguages')}
            value={t('settings.values.selectedCount', { count: p?.knownLanguages?.length ?? 0 })}
            warn={!p?.knownLanguages?.length}
            onClick={() => setPicker('knownLanguages')}
            chevron
            last
          />
        </div>

        {/* LOCATION */}
        <SectionHeader label={t('settings.sections.location')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsRow
            icon="home"
            tone="amber"
            label={t('settings.rows.homeCountry')}
            value={getCountryName(p?.homeCountry) ?? t('settings.notSet')}
            warn={!p?.homeCountry}
            onClick={() => setPicker('homeCountry')}
            chevron
          />
          <SettingsRow
            icon="pin"
            tone="purple"
            label={t('settings.rows.currentCountry')}
            value={getCountryName(p?.currentCountry) ?? t('settings.notSet')}
            warn={!p?.currentCountry}
            onClick={() => setPicker('currentCountry')}
            chevron
            last
          />
        </div>

        {/* ABOUT YOU */}
        <SectionHeader label={t('settings.sections.about')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsRow
            icon="briefcase"
            tone="green"
            label={t('settings.rows.lifeStage')}
            value={
              p?.lifeStage
                ? tLoose(LIFE_STAGE_KEYS[p.lifeStage as keyof typeof LIFE_STAGE_KEYS] ?? '') || p.lifeStage
                : t('settings.notSet')
            }
            warn={!p?.lifeStage}
            onClick={() => setPicker('lifeStage')}
            chevron
          />
          <SettingsRow
            icon="document"
            tone="purple"
            label={t('settings.rows.residenceStatus')}
            value={
              p?.visaType
                ? tLoose(VISA_TYPE_KEYS[p.visaType as keyof typeof VISA_TYPE_KEYS] ?? '') || p.visaType
                : t('settings.notSet')
            }
            warn={!p?.visaType}
            onClick={() => setPicker('visaType')}
            chevron
          />
          <SettingsRow
            icon="time"
            tone="amber"
            label={t('settings.rows.timeInCountry')}
            value={
              p?.timeInCountry
                ? tLoose(TIME_IN_COUNTRY_KEYS[p.timeInCountry as keyof typeof TIME_IN_COUNTRY_KEYS] ?? '') || p.timeInCountry
                : t('settings.notSet')
            }
            warn={!p?.timeInCountry}
            onClick={() => setPicker('timeInCountry')}
            chevron
          />
          <SettingsRow
            icon="user"
            tone="blue"
            label={t('settings.rows.ageRange')}
            value={
              p?.ageRange
                ? tLoose(AGE_RANGE_KEYS[p.ageRange as keyof typeof AGE_RANGE_KEYS] ?? '') || p.ageRange
                : t('settings.notSet')
            }
            warn={!p?.ageRange}
            onClick={() => setPicker('ageRange')}
            chevron
          />
          <SettingsRow
            icon="heart"
            tone="pink"
            label={t('settings.rows.interests')}
            value={
              p?.interests?.length
                ? t('settings.values.selectedCount', { count: p.interests.length })
                : t('settings.notSet')
            }
            warn={!p?.interests?.length}
            onClick={() => setPicker('interests')}
            chevron
            last
          />
        </div>
        {p?.interests && p.interests.length > 0 && (
          <div className="nw-interest-chips">
            {p.interests.map((i) => (
              <span key={i} className="nw-interest-chip">
                {tLoose(INTEREST_LABEL_KEY_BY_SLUG[i] ?? '') || i}
              </span>
            ))}
          </div>
        )}

        {/* PREFERENCES */}
        <SectionHeader id="section-prefs" label={t('settings.sections.preferences')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsToggleRow
            icon="moon"
            tone="purple"
            label={t('settings.rows.darkMode')}
            on={mode === 'dark'}
            onChange={(v) => {
              setMode(v ? 'dark' : 'light');
              updateProfile.mutate({ themePreference: v ? 'dark' : 'light' });
            }}
          />
          <SettingsToggleRow
            icon="bell"
            tone="amber"
            label={t('settings.rows.notifications')}
            on={notifPrefs.data?.pushEnabled ?? true}
            onChange={(v) => updatePrefs.mutate({ pushEnabled: v })}
          />
          <SettingsToggleRow
            icon="warning"
            tone="red"
            label={t('settings.rows.criticalAlerts')}
            on={notifPrefs.data?.criticalAlerts ?? true}
            onChange={(v) => updatePrefs.mutate({ criticalAlerts: v })}
          />
          <SettingsToggleRow
            icon="info"
            tone="amber"
            label={t('settings.rows.importantNotices')}
            on={notifPrefs.data?.importantNotices ?? false}
            onChange={(v) => updatePrefs.mutate({ importantNotices: v })}
            last
          />
        </div>

        {/* ACCOUNT */}
        <SectionHeader id="section-account" label={t('settings.sections.account')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsRow
            icon="lock"
            tone="blue"
            label={t('settings.rows.privacyLegal')}
            onClick={() => window.open('https://legal.thenewswave.app/privacy', '_blank')}
            chevron
          />
          <SettingsRow
            icon="key"
            tone="amber"
            label={t('settings.rows.changePassword')}
            onClick={onChangePassword}
            chevron
            last
          />
        </div>

        {/* DESTRUCTIVE */}
        <SectionHeader id="section-account-actions" label={t('web.profile.sidebar.accountActions')} />
        <div className="nw-card-block nw-settings-group">
          <SettingsRow
            icon="signout"
            tone="red"
            label={t('settings.rows.signOut')}
            destructive
            onClick={onLogout}
          />
          <SettingsRow
            icon="trash"
            tone="red"
            label={t('settings.rows.deleteAccount')}
            destructive
            onClick={() => setConfirmDelete(true)}
            last
          />
        </div>

        <p className="nw-profile-footer">
          {memberSince ? `${t('web.profile.hero.memberSince', { date: memberSince })} · ` : ''}{t('web.profile.footer.version')}
        </p>
      </section>

      {/* Pickers */}
      {picker === 'primaryLanguage' && (
        <PickerModal
          title={t('web.profile.modals.primaryLanguage.title')}
          subtitle={t('web.profile.modals.primaryLanguage.subtitle')}
          options={languageOptions}
          selected={p?.primaryLanguage ?? 'en'}
          searchable
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            const code = String(value);
            updateProfile.mutate({ primaryLanguage: code });
            closePicker();
          }}
        />
      )}
      {picker === 'knownLanguages' && (
        <PickerModal
          title={t('web.profile.modals.knownLanguages.title')}
          subtitle={t('web.profile.modals.knownLanguages.subtitle')}
          options={knownLanguageOptions}
          selected={p?.knownLanguages ?? []}
          multi
          searchable
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            // Backend enforces max 5; client mirrors that cap to avoid
            // a 422 round-trip.
            const next = (value as string[]).slice(0, 5);
            updateProfile.mutate({ knownLanguages: next });
            closePicker();
          }}
        />
      )}
      {picker === 'homeCountry' && (
        <PickerModal
          title={t('web.profile.modals.homeCountry.title')}
          options={countryOptions}
          selected={p?.homeCountry ?? ''}
          searchable
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ homeCountry: String(value) });
            closePicker();
          }}
        />
      )}
      {picker === 'currentCountry' && (
        <PickerModal
          title={t('web.profile.modals.currentCountry.title')}
          subtitle={t('web.profile.modals.currentCountry.subtitle')}
          options={countryOptions}
          selected={p?.currentCountry ?? ''}
          searchable
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ currentCountry: String(value) });
            closePicker();
          }}
        />
      )}
      {picker === 'lifeStage' && (
        <PickerModal
          title={t('web.profile.modals.lifeStage.title')}
          options={lifeStageOptions}
          selected={p?.lifeStage ?? ''}
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ lifeStage: String(value) });
            closePicker();
          }}
        />
      )}
      {picker === 'visaType' && (
        <PickerModal
          title={t('web.profile.modals.residenceStatus.title')}
          options={visaOptions}
          selected={p?.visaType ?? ''}
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ visaType: String(value) });
            closePicker();
          }}
        />
      )}
      {picker === 'timeInCountry' && (
        <PickerModal
          title={t('web.profile.modals.timeInCountry.title')}
          options={timeInCountryOptions}
          selected={p?.timeInCountry ?? ''}
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ timeInCountry: String(value) });
            closePicker();
          }}
        />
      )}
      {picker === 'ageRange' && (
        <PickerModal
          title={t('web.profile.modals.ageRange.title')}
          options={ageRangeOptions}
          selected={p?.ageRange ?? ''}
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ ageRange: String(value) });
            closePicker();
          }}
        />
      )}
      {picker === 'interests' && (
        <PickerModal
          title={t('web.profile.modals.interests.title')}
          subtitle={t('web.profile.modals.interests.subtitle')}
          options={interestOptions}
          selected={p?.interests ?? []}
          multi
          onClose={closePicker}
          pending={updateProfile.isPending}
          onConfirm={(value) => {
            updateProfile.mutate({ interests: value as string[] });
            closePicker();
          }}
        />
      )}

      {confirmDelete && (
        <div className="nw-modal-bg" role="dialog" aria-modal="true" onClick={() => setConfirmDelete(false)}>
          <div className="nw-modal" role="document" onClick={(e) => e.stopPropagation()}>
            <h2 className="nw-modal__title">{t('web.profile.deleteModal.title')}</h2>
            <p className="nw-modal__sub">{t('web.profile.deleteModal.body')}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="nw-btn nw-btn--ghost" onClick={() => setConfirmDelete(false)}>
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                className="nw-btn nw-btn--primary"
                style={{ background: 'var(--error)', boxShadow: 'none' }}
                onClick={onDeleteAccount}
                disabled={deleteAccount.isPending}
              >
                {deleteAccount.isPending
                  ? t('web.profile.deleteModal.deleting')
                  : t('settings.rows.deleteAccount')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeader({ id, label }: { readonly id?: string; readonly label: string }) {
  return (
    <div id={id} className="nw-profile-section-label">
      {label}
    </div>
  );
}

function FlagSquare({ code }: { readonly code: string | null | undefined }) {
  return (
    <span className="nw-flag-square" aria-hidden>
      {getFlag(code) || '🏳️'}
    </span>
  );
}

type Tone = 'amber' | 'blue' | 'green' | 'red' | 'purple' | 'pink';
type IconName =
  | 'globe'
  | 'book'
  | 'bookmark'
  | 'home'
  | 'pin'
  | 'moon'
  | 'bell'
  | 'lock'
  | 'key'
  | 'trash'
  | 'signout'
  | 'document'
  | 'time'
  | 'user'
  | 'heart'
  | 'briefcase'
  | 'warning'
  | 'info';

const TONE_PALETTE: Record<Tone, [string, string]> = {
  amber: ['#f0a030', 'rgba(240,160,48,0.16)'],
  blue: ['#4a9eff', 'rgba(74,158,255,0.16)'],
  green: ['#7ac060', 'rgba(122,192,96,0.16)'],
  red: ['#e05555', 'rgba(224,85,85,0.16)'],
  purple: ['#a090e8', 'rgba(160,144,232,0.16)'],
  pink: ['#f472b6', 'rgba(244,114,182,0.16)'],
};

function pickIcon(name: IconName, color: string) {
  const props = { size: 17, stroke: 2 as const, color, style: { color } };
  switch (name) {
    case 'globe':     return <GlobeIcon {...props} />;
    case 'book':      return <BookIcon {...props} />;
    case 'bookmark':  return <BookmarkIcon {...props} />;
    case 'home':      return <HomeIcon {...props} />;
    case 'pin':       return <PinIcon {...props} />;
    case 'moon':      return <MoonIcon {...props} />;
    case 'bell':      return <BellIcon {...props} />;
    case 'lock':      return <LockGlyph color={color} />;
    case 'key':       return <KeyGlyph color={color} />;
    case 'trash':     return <TrashGlyph color={color} />;
    case 'signout':   return <SignOutGlyph color={color} />;
    case 'document':  return <DocumentGlyph color={color} />;
    case 'time':      return <TimeGlyph color={color} />;
    case 'user':      return <UserIcon {...props} />;
    case 'heart':     return <HeartGlyph color={color} />;
    case 'briefcase': return <BriefcaseGlyph color={color} />;
    case 'warning':   return <WarningGlyph color={color} />;
    case 'info':      return <InfoGlyph color={color} />;
  }
}

interface RowProps {
  readonly icon: IconName;
  readonly tone: Tone;
  readonly label: string;
  readonly value?: string;
  readonly onClick?: () => void;
  readonly warn?: boolean;
  readonly chevron?: boolean;
  readonly destructive?: boolean;
  readonly last?: boolean;
}

function SettingsRow({ icon, tone, label, value, onClick, warn, chevron, destructive, last }: RowProps) {
  const [fg, bg] = destructive ? ['#e05555', 'rgba(224,85,85,0.16)'] : TONE_PALETTE[tone];
  return (
    <button
      type="button"
      className={`nw-settings-row ${last ? 'is-last' : ''}`}
      onClick={onClick}
      disabled={!onClick}
    >
      <span className="nw-settings-row__icon" style={{ background: bg, color: fg }}>
        {pickIcon(icon, fg)}
      </span>
      <span
        className="nw-settings-row__label"
        style={destructive ? { color: 'var(--error)' } : undefined}
      >
        {label}
      </span>
      {value && (
        <span
          className="nw-settings-row__value"
          style={warn ? { color: 'var(--warning)' } : undefined}
        >
          {value}
        </span>
      )}
      {chevron && <ChevronIcon dir="right" size={14} stroke={2.2} />}
    </button>
  );
}

interface ToggleRowProps {
  readonly icon: IconName;
  readonly tone: Tone;
  readonly label: string;
  readonly on: boolean;
  readonly onChange: (v: boolean) => void;
  readonly last?: boolean;
}

function SettingsToggleRow({ icon, tone, label, on, onChange, last }: ToggleRowProps) {
  const [fg, bg] = TONE_PALETTE[tone];
  return (
    <div className={`nw-settings-row ${last ? 'is-last' : ''}`}>
      <span className="nw-settings-row__icon" style={{ background: bg, color: fg }}>
        {pickIcon(icon, fg)}
      </span>
      <span className="nw-settings-row__label">{label}</span>
      <input
        type="checkbox"
        className="nw-toggle"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
    </div>
  );
}

// --- Inline glyphs for the rows mobile uses but icons.tsx doesn't expose ---

function LockGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}
function KeyGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="13" r="4" />
      <path d="M11 11l9-9" />
      <path d="M16 5l3 3" />
    </svg>
  );
}
function TrashGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 7h14" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
      <path d="M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13" />
    </svg>
  );
}
function SignOutGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
function DocumentGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
      <path d="M14 3v6h6" />
      <path d="M8 13h8" />
      <path d="M8 17h6" />
    </svg>
  );
}
function TimeGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function HeartGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 21s-7-5-7-11a4 4 0 017-2 4 4 0 017 2c0 6-7 11-7 11z" />
    </svg>
  );
}
function BriefcaseGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
      <path d="M3 13h18" />
    </svg>
  );
}
function WarningGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v5" />
      <circle cx="12" cy="18" r="0.5" fill={color} />
    </svg>
  );
}
function InfoGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <circle cx="12" cy="8" r="0.5" fill={color} />
    </svg>
  );
}
